import React, { useEffect, useImperativeHandle, useState } from 'react';

import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Divider } from 'primereact/divider';

import { TaskTypeRequiredFields } from '../types/interfaces/TaskType';

import './css/FieldFillingControl.css';
import ConfirmationDialog from './shared/ConfirmationDialog';

interface RefInterface {
  handleCancel: () => void;
}

interface TaskTypeRequiredFieldsControlProps {
  requiredFields: TaskTypeRequiredFields;
  onUpdate: (updatedData: TaskTypeRequiredFields) => void;
  closeDialog: () => void;
}

interface RequiredFieldRow {
  key?: keyof TaskTypeRequiredFields;
  label: string;
  isAlwaysRequired?: boolean;
}

const REQUIRED_FIELD_ROWS: RequiredFieldRow[] = [
  { key: 'name', label: 'Название' },
  { label: 'Тип наряда', isAlwaysRequired: true },
  { key: 'asset', label: 'Объект' },
  { key: 'department', label: 'Участок' },
  { key: 'employees', label: 'Исполнители' },
  { key: 'plannedStartDate', label: 'Начало работы (план)' },
  { key: 'actualStartDate', label: 'Начало работы (факт)' },
  { key: 'plannedEndDate', label: 'Завершение работы (план)' },
  { key: 'actualEndDate', label: 'Завершение работы (факт)' },
  { key: 'description', label: 'Описание' },
  { key: 'plannedOperations', label: 'Плановые работы' },
  { key: 'actualOperations', label: 'Фактические работы' }
];

const TaskTypeRequiredFieldsControl = React.forwardRef<
  RefInterface | null,
  TaskTypeRequiredFieldsControlProps
>(({ requiredFields, onUpdate, closeDialog }, ref) => {
  const [localRequiredFields, setLocalRequiredFields] =
    useState<TaskTypeRequiredFields>({
      ...requiredFields,
      attachment: false
    });
  const [showDialog, setShowDialog] = useState(false);
  const [unsavedChanges, setUnsavedChanges] = useState(false);

  useEffect(() => {
    setLocalRequiredFields({
      ...requiredFields,
      attachment: false
    });
    setUnsavedChanges(false);
  }, [requiredFields]);

  const handleCheckboxChange = (key: keyof TaskTypeRequiredFields) => {
    setLocalRequiredFields((previousValue) => ({
      ...previousValue,
      [key]: !previousValue[key]
    }));
    setUnsavedChanges(true);
  };

  const handleCancel = () => {
    if (unsavedChanges) {
      setShowDialog(true);
    } else {
      closeDialog();
    }
  };

  useImperativeHandle(ref, () => ({
    handleCancel
  }));

  const handleConfirmationContinue = () => {
    setShowDialog(false);
    closeDialog();
  };

  return (
    <div className="dialog-tabless-card">
      <div className="flex flex-column pb-0">
        <div className="dialog-tabless-card__header-gap flex flex-column">
          <Divider />
          <div className="field-filling-constructor-card">
            <div
              className="scrollable-container"
              style={{ width: '100%', maxHeight: 'calc(90vh - 190px)', overflow: 'auto' }}
            >
              <div className="flex flex-column scrollable-content" style={{ width: '99%' }}>
                {REQUIRED_FIELD_ROWS.map((item, index) => (
                  <div
                    key={item.key ?? 'taskType'}
                    className="field-filling-constructor-card__row"
                  >
                    <div
                      className="flex flex-row justify-content-between align-items-center gap-3 pl-3 pr-3"
                      style={{
                        marginBottom:
                          index !== REQUIRED_FIELD_ROWS.length - 1 ? '16px' : ''
                      }}
                    >
                      <label style={{ width: 265 }}>{item.label}</label>
                      <div className="flex align-items-center">
                        <Checkbox
                          inputId={`required-field-${item.key ?? 'taskType'}`}
                          checked={
                            item.isAlwaysRequired ||
                            (item.key ? localRequiredFields[item.key] : false)
                          }
                          disabled={item.isAlwaysRequired}
                          onChange={() => {
                            if (item.key) {
                              handleCheckboxChange(item.key);
                            }
                          }}
                        />
                        <label
                          style={{ marginLeft: '14px' }}
                          htmlFor={`required-field-${item.key ?? 'taskType'}`}
                        >
                          Обязательный
                        </label>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="default-card__buttons">
        <div className="flex gap-3">
          <Button
            label="Принять"
            icon="pi pi-check"
            onClick={() => onUpdate(localRequiredFields)}
          />
          <Button
            label="Отмена"
            icon="pi pi-times"
            onClick={handleCancel}
            className="p-button-secondary"
          />
        </div>
      </div>

      <ConfirmationDialog
        visible={showDialog}
        onCancel={() => setShowDialog(false)}
        onContinue={handleConfirmationContinue}
      />
    </div>
  );
});

TaskTypeRequiredFieldsControl.displayName = 'TaskTypeRequiredFieldsControl';

export default TaskTypeRequiredFieldsControl;
